
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Search, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { fetchLogs, clearLogs } from "@/lib/logger";

interface Log {
  id: string;
  level: "error" | "warning" | "info";
  message: string;
  location: string;
  details: string | null;
  user_id: string | null;
  user_email: string | null;
  created_at: string;
}

export function LogsViewer() {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState<string>("all");
  const [selectedLog, setSelectedLog] = useState<Log | null>(null);
  const [isClearing, setIsClearing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Fetch logs with search and filter
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['logs', searchTerm, levelFilter],
    queryFn: async () => {
      try {
        const result = await fetchLogs({
          search: searchTerm,
          level: levelFilter !== "all" ? levelFilter : "",
          limit: 100,
          offset: 0
        });
        return result;
      } catch (err: any) {
        throw new Error(`Error fetching logs: ${err.message}`);
      }
    },
  });

  const logs = data?.logs as Log[] | undefined;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    refetch();
  };

  const handleClearLogs = async () => {
    setIsClearing(true);
    try {
      const result = await clearLogs(levelFilter);
      toast({
        title: "Logs cleared",
        description: result.message || `Successfully cleared logs${levelFilter !== "all" ? ` with level '${levelFilter}'` : ''}`,
      });
      refetch(); // Refresh the logs list
      setShowClearConfirm(false);
    } catch (err: any) {
      toast({
        title: "Error",
        description: `Failed to clear logs: ${err.message}`,
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  const getLevelBadge = (level: string) => {
    switch (level) {
      case "error":
        return <Badge variant="destructive">Error</Badge>;
      case "warning":
        return <Badge variant="default" className="bg-yellow-500">Warning</Badge>;
      case "info":
        return <Badge variant="secondary">Info</Badge>;
      default:
        return <Badge variant="outline">{level}</Badge>;
    }
  };

  // Format JSON details for display
  const formatDetails = (details: string | null) => {
    if (!details) return "No details available";
    
    try {
      const parsed = JSON.parse(details);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return details;
    }
  };

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-destructive">Error loading logs: {error.toString()}</p>
        <Button 
          onClick={() => refetch()} 
          variant="outline" 
          className="mt-4"
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">System Logs</h2>
        <div className="flex gap-2">
          <Button 
            onClick={() => refetch()}
            variant="outline"
            size="sm"
          >
            Refresh
          </Button>
          <Button 
            onClick={() => setShowClearConfirm(true)}
            variant="destructive"
            size="sm"
            disabled={isLoading || isClearing || !logs?.length}
          >
            <Trash2 className="mr-1 h-4 w-4" />
            Clear Logs
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button type="submit">Search</Button>
        </form>
        
        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="error">Errors</SelectItem>
            <SelectItem value="warning">Warnings</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="h-96 flex items-center justify-center">
          <p className="text-muted-foreground">Loading logs...</p>
        </div>
      ) : logs?.length === 0 ? (
        <div className="h-64 flex items-center justify-center border rounded-md">
          <p className="text-muted-foreground">No logs found</p>
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Level</TableHead>
                <TableHead className="w-1/3">Message</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>User</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs?.map((log) => (
                <TableRow 
                  key={log.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSelectedLog(log)}
                >
                  <TableCell className="font-mono text-xs">
                    {format(new Date(log.created_at), "yyyy-MM-dd HH:mm:ss")}
                  </TableCell>
                  <TableCell>{getLevelBadge(log.level)}</TableCell>
                  <TableCell className="truncate max-w-[300px]">
                    {log.message}
                  </TableCell>
                  <TableCell className="truncate max-w-[200px]">
                    {log.location}
                  </TableCell>
                  <TableCell>
                    {log.user_email ? (
                      <span className="text-xs">{log.user_email}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">No user</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Log Details Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedLog && getLevelBadge(selectedLog.level)}
              <span>{selectedLog?.message}</span>
            </DialogTitle>
            <DialogDescription>
              {selectedLog && format(new Date(selectedLog.created_at), "PPpp")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium">Location</h4>
                <p className="text-sm text-muted-foreground">{selectedLog?.location}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium">User</h4>
                <p className="text-sm text-muted-foreground">
                  {selectedLog?.user_email || 'No user'}
                  {selectedLog?.user_id && <span className="block text-xs text-muted-foreground">ID: {selectedLog.user_id}</span>}
                </p>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-1">Details</h4>
              <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto">
                {selectedLog && formatDetails(selectedLog.details)}
              </pre>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setSelectedLog(null)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Logs Confirmation Alert */}
      <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {levelFilter !== "all" ? `all logs with level '${levelFilter}'` : "all logs"}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClearing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleClearLogs();
              }}
              disabled={isClearing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isClearing ? "Clearing..." : "Clear Logs"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
